import { TipoIdentificacion } from '../../domain/model/maestro/entity/tipo-identificacion.model';
import { PacienteCoberturaNh } from '../../domain/model/no-hospitalario/solicitud/dto/paciente-cobertura-nh-dto';
import { PacienteCobertura } from '../../domain/model/remision/dto/paciente-cobertura-dto';
import { Convenio } from '../../domain/model/remision/entity/convenio.model';
import { PlanSalud } from '../../domain/model/maestro/entity/plan-salud.model';

const NOMBRE_MARIA_ELENA = 'María Elena';
const APELLIDO_GONZALEZ_LOPEZ = 'González López';
const FECHA_NACIMIENTO_MARIA = 402469200000;
const EDAD_MARIA = '40';
const SEXO_MASCULINO = 'Masculino';
const SEXO_FEMENINO = 'Femenino';
const ESTADO_CIVIL_CASADA = 'Casada';
const DESCRIPCION_PLAN_PREMIUM = 'Plan Premium';
const NIT_PLAN_PREMIUM = '800123457';
const NOMBRE_AFILIACION_POLIZA = 'PÓLIZA';
const NOMBRE_ASEGURADORA_SURA_SEGUROS = 'SURA SEGUROS';
const NOMBRE_ASEGURADORA_SURA_EPS = 'SURA EPS';
const DESCRIPCION_POLIZA_CLASICA = 'Póliza Clásica';
const IPS_BASICA_SURA_NORTE = 'IPS SURA SEDE NORTE';
const UNIDAD_EDAD_ANIOS = 'AÑOS';
const PLAN_COMPLEMENTARIO_A = 'PLAN_COMPLEMENTARIO_A';
const PISO_COVID_2 = 'PISO_2';
const NOMBRE_PERSONA_AUTORIZA_MARIA = 'María Elena González López';
const NOMBRE_CARLOS_ALBERTO = 'Carlos Alberto';
const APELLIDO_RODRIGUEZ_SILVA = 'Rodríguez Silva';
const FECHA_NACIMIENTO_CARLOS = 315529200000; // 1980-01-01
const EDAD_CARLOS = '45';
const OCUPACION_INGENIERO = 'Ingeniero';
const CORREO_MARIA = 'maria.gonzalez@email.com';
const CORREO_CARLOS = 'carlos.rodriguez@email.com';
const TIPO_PLAN_SALUD_POLIZA = 'POLIZA';
const TIPO_PLAN_SALUD_PAC = 'PAC';
const DESCRIPCION_PLAN_COMPLEMENTARIO = 'PAC PLAN COMPLEMENTARIO';
const CODIGO_RAMO_091 = '091';
const NUMERO_PLAN_3_CLASICO = '3|CLÁSICO';
const CODIGO_PRODUCTO_16 = '16';

interface VigenciaConvenioMock {
  fechaInicio: number;
  fechaFin: number;
  numeroContrato: string;
}

interface CoberturasMock {
  tienePoliza: boolean;
  tienePac: boolean;
  tienePOS: boolean;
  tieneArl: boolean;
}

type DatosConvenioMock = Pick<Convenio,
  'planContrato' | 'codigoRamo' | 'tipoPlanSalud' | 'descripcionPlanContrato' | 'numeroPlan' | 'codigoProducto'>;

const VIGENCIA_CNT_001: VigenciaConvenioMock = {
  fechaInicio: 1735689600000, // 2025-01-01
  fechaFin: 1767225599000,    // 2025-12-31
  numeroContrato: 'CNT-001'
};

const VIGENCIA_CNT_002: VigenciaConvenioMock = {
  fechaInicio: 1743397200000,
  fechaFin: 1774933200000,
  numeroContrato: 'CNT-002'
};

const CONVENIO_POLIZA_CLASICA: DatosConvenioMock = {
  planContrato: '074',
  codigoRamo: CODIGO_RAMO_091,
  tipoPlanSalud: TIPO_PLAN_SALUD_POLIZA,
  descripcionPlanContrato: DESCRIPCION_POLIZA_CLASICA,
  numeroPlan: '2|CLÁSICO',
  codigoProducto: '15'
};

const CONVENIO_SALUD_PARA_DOS: DatosConvenioMock = {
  planContrato: 'STC',
  codigoRamo: '081',
  tipoPlanSalud: TIPO_PLAN_SALUD_POLIZA,
  descripcionPlanContrato: 'Salud para Dos',
  numeroPlan: NUMERO_PLAN_3_CLASICO,
  codigoProducto: CODIGO_PRODUCTO_16
};

const CONVENIO_POLIZA_CLASICA_FAMILIAR: DatosConvenioMock = {
  planContrato: '056',
  codigoRamo: '080',
  tipoPlanSalud: TIPO_PLAN_SALUD_POLIZA,
  descripcionPlanContrato: 'Póliza Clásica Familiar',
  numeroPlan: NUMERO_PLAN_3_CLASICO,
  codigoProducto: CODIGO_PRODUCTO_16
};

const CONVENIO_PAC_PLAN_COMPLEMENTARIO: DatosConvenioMock = {
  planContrato: 'PVC',
  codigoRamo: CODIGO_RAMO_091,
  tipoPlanSalud: TIPO_PLAN_SALUD_PAC,
  descripcionPlanContrato: DESCRIPCION_PLAN_COMPLEMENTARIO,
  numeroPlan: '5|PREMIUM',
  codigoProducto: '06'
};

const crearConvenio = (datos: DatosConvenioMock, vigencia: VigenciaConvenioMock): Convenio => ({
  ...datos,
  tipoRamo: 'C',
  anexoUrgencias: false,
  anexoConsultaExterna: false,
  anexoAtencionDomiciliaria: true,
  fechaInicioVigenciaAsegurado: vigencia.fechaInicio as any, // ✅ Cast a any
  fechaFinVigenciaAsegurado: vigencia.fechaFin as any,
  numeroContrato: vigencia.numeroContrato
});

const crearTipoIdentificacionCC = (): TipoIdentificacion =>
  new TipoIdentificacion('1', 'CC', 'Cédula de Ciudadanía', '13', 'C');

const crearAfiliacionPoliza = (): PlanSalud => ({
  id: '3',
  nombre: NOMBRE_AFILIACION_POLIZA,
  nombreAseguradora: NOMBRE_ASEGURADORA_SURA_SEGUROS,
  idPlan: '3'
});

const crearAfiliacionPos = (): PlanSalud => ({
  id: '1',
  nombre: 'POS',
  nombreAseguradora: NOMBRE_ASEGURADORA_SURA_EPS,
  idPlan: '1'
});

const crearPacienteMaria = (
  numeroIdentificacion: string,
  convenios: Convenio[],
  coberturas: CoberturasMock
): PacienteCobertura => new PacienteCobertura(
  null,                              // idRemisionPK
  NOMBRE_MARIA_ELENA,                // nombre
  APELLIDO_GONZALEZ_LOPEZ,           // apellido
  crearTipoIdentificacionCC(),       // tipoIdentificacion
  numeroIdentificacion,              // numeroIdentificacion
  FECHA_NACIMIENTO_MARIA as any,     // fechaNacimiento
  EDAD_MARIA,                        // edad
  SEXO_MASCULINO,                    // sexo
  ESTADO_CIVIL_CASADA,               // estadoCivil
  {                                  // tipoPlanParticular
    id: '2',
    idTipoPlanParticular: '2',
    descripcion: DESCRIPCION_PLAN_PREMIUM,
    nit: NIT_PLAN_PREMIUM
  },
  null,                              // ocupacion
  null,                              // email
  null,                              // tipoAsegurador
  null,                              // estadoSuspension
  true,                              // coberturaDomiciliaria
  null,                              // fechaLimiteCobertura
  crearAfiliacionPoliza(),           // tipoAfiliacion
  convenios,                         // convenios
  null,                              // nivelIngreso
  IPS_BASICA_SURA_NORTE,             // ipsBasicaAsignada
  null,                              // lugarAtencion
  UNIDAD_EDAD_ANIOS,                 // unidadEdad
  null,                              // bebeCanguro
  '',                                // codigoARL
  null,                              // medidaDepeso
  null,                              // peso
  null,                              // codigoSiniestro
  PLAN_COMPLEMENTARIO_A,             // planComplementario
  true,                              // esEditable
  null,                              // priorizacion
  null,                              // fechaConsultaMedica
  null,                              // fechaInicioSintomas
  null,                              // fechaTomaMuestra
  null,                              // tipoCasoCovid
  null,                              // integracion
  null,                              // observacion
  null,                              // pacienteSinFechaNacimiento
  null,                              // encounterId
  null,                              // embarazada
  null,                              // oxigenoDependiente
  null,                              // medicoCreadorTipo
  null,                              // medicoCreadorNumero
  null,                              // codigoSexo
  null,                              // descSexo
  null,                              // codigoPlan
  null,                              // descPlan
  null,                              // descMunicipio
  null,                              // codigoDaneMunicipio
  null,                              // descDepartamento
  null,                              // codigoDaneDpto
  PISO_COVID_2,                      // pisoCovid
  null,                              // subNivel
  null,                              // vacunacion
  NOMBRE_PERSONA_AUTORIZA_MARIA,     // nombrePersonaAutoriza
  coberturas.tienePoliza,            // tienePoliza
  coberturas.tienePac,               // tienePac
  coberturas.tienePOS,               // tienePOS
  coberturas.tieneArl                // tieneArl
);

const crearPacienteCarlosNh = (
  numeroIdentificacion: string,
  convenios: Convenio[],
  coberturas: CoberturasMock
): PacienteCoberturaNh => ({
  tipoIdentificacion: crearTipoIdentificacionCC(),
  numeroIdentificacion,
  nombre: NOMBRE_CARLOS_ALBERTO,
  apellido: APELLIDO_RODRIGUEZ_SILVA,
  fechaNacimiento: new Date(FECHA_NACIMIENTO_CARLOS),
  edad: EDAD_CARLOS,
  sexo: SEXO_MASCULINO,
  ocupacion: OCUPACION_INGENIERO,
  email: CORREO_CARLOS,
  tipoAfiliacion: crearAfiliacionPos(),
  codigoARL: null,
  celular: '3009876543',
  telefono: '6017654321',
  convenios,
  ...coberturas
});

export class PacientesMockData {

  /**
   * Array de pacientes simulados para testing
   */
  public static readonly PACIENTES_SIMULADOS: PacienteCobertura[] = [

    // ✅ PACIENTE CON DOCUMENTO 1
    crearPacienteMaria(
      '1',
      [crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_002)],
      { tienePoliza: true, tienePac: false, tienePOS: false, tieneArl: true }
    ),

    // ✅ PACIENTE CON DOCUMENTO 2
    crearPacienteMaria(
      '2',
      [crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_002)],
      { tienePoliza: true, tienePac: false, tienePOS: false, tieneArl: false }
    ),

    // ✅ PACIENTE CON DOCUMENTO 3
    crearPacienteMaria(
      '3',
      [crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_002)],
      { tienePoliza: true, tienePac: false, tienePOS: true, tieneArl: false }
    ),

    // ✅ PACIENTE CON DOCUMENTO 4
    crearPacienteMaria(
      '4',
      [
        crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_002),
        crearConvenio(CONVENIO_SALUD_PARA_DOS, VIGENCIA_CNT_002),
        crearConvenio(CONVENIO_PAC_PLAN_COMPLEMENTARIO, VIGENCIA_CNT_002)
      ],
      { tienePoliza: false, tienePac: true, tienePOS: true, tieneArl: true }
    ),

    // ✅ PACIENTE CON DOCUMENTO 5
    crearPacienteMaria(
      '5',
      [
        crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_002),
        crearConvenio(CONVENIO_SALUD_PARA_DOS, VIGENCIA_CNT_002),
        crearConvenio(CONVENIO_PAC_PLAN_COMPLEMENTARIO, VIGENCIA_CNT_002)
      ],
      { tienePoliza: true, tienePac: true, tienePOS: true, tieneArl: false }
    )

  ];

  /**
   * Busca un paciente simulado por número de documento
   * @param documento - Número de documento a buscar
   * @returns PacienteCobertura o null si no se encuentra
   */
  public static buscarPacientePorDocumento(documento: string): PacienteCobertura | null {
    return this.PACIENTES_SIMULADOS.find(paciente =>
      paciente.numeroIdentificacion === documento
    ) || null;
  }

  public static readonly PACIENTES_SIMULADOS_NH: PacienteCoberturaNh[] = [

    // ✅ PACIENTE CON DOCUMENTO 1
    {
      tipoIdentificacion: crearTipoIdentificacionCC(),
      numeroIdentificacion: '1',
      nombre: NOMBRE_MARIA_ELENA,
      apellido: APELLIDO_GONZALEZ_LOPEZ,
      fechaNacimiento: new Date(FECHA_NACIMIENTO_MARIA), // ✅ Como Date object
      edad: EDAD_MARIA,
      sexo: SEXO_FEMENINO,
      ocupacion: 'Empleada',
      email: CORREO_MARIA,
      tipoAfiliacion: crearAfiliacionPoliza(),
      codigoARL: '',
      celular: '3001234567',
      telefono: '6013456789',
      convenios: [crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_001)],
      tienePoliza: true,
      tienePac: false,
      tienePOS: false,
      tieneArl: true
    },

    // ✅ PACIENTE CON DOCUMENTO 2
    crearPacienteCarlosNh(
      '2',
      [
        crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_001),
        crearConvenio(CONVENIO_POLIZA_CLASICA_FAMILIAR, VIGENCIA_CNT_001)
      ],
      { tienePoliza: true, tienePac: false, tienePOS: false, tieneArl: false }
    ),

    // ✅ PACIENTE CON DOCUMENTO 3
    crearPacienteCarlosNh(
      '3',
      [
        crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_001),
        crearConvenio(CONVENIO_POLIZA_CLASICA_FAMILIAR, VIGENCIA_CNT_001)
      ],
      { tienePoliza: true, tienePac: false, tienePOS: true, tieneArl: false }
    ),

    // ✅ PACIENTE CON DOCUMENTO 4
    crearPacienteCarlosNh(
      '4',
      [crearConvenio(CONVENIO_PAC_PLAN_COMPLEMENTARIO, VIGENCIA_CNT_002)],
      { tienePoliza: false, tienePac: true, tienePOS: true, tieneArl: true }
    ),

    // ✅ PACIENTE CON DOCUMENTO 5
    crearPacienteCarlosNh(
      '5',
      [
        crearConvenio(CONVENIO_POLIZA_CLASICA, VIGENCIA_CNT_001),
        crearConvenio(CONVENIO_PAC_PLAN_COMPLEMENTARIO, VIGENCIA_CNT_002)
      ],
      { tienePoliza: true, tienePac: true, tienePOS: true, tieneArl: false }
    )

  ];

  public static buscarPacientePorDocumentoNH(documento: string): PacienteCoberturaNh | null {
    return this.PACIENTES_SIMULADOS_NH.find(paciente =>
      paciente.numeroIdentificacion === documento
    ) || null;
  }
}
